import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type CropPresetKey = 'free' | 'square' | 'landscape' | 'wide' | 'portrait'
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se'
type OutputMime = 'image/jpeg' | 'image/png' | 'image/webp'

interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

interface Point {
  x: number
  y: number
}

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface CropPreview {
  blob: Blob
  url: string
}

interface DragState {
  mode: 'draw' | 'move' | `resize-${ResizeHandle}`
  startPointer: Point
  startCrop: CropRect
  pointerId: number
}

type CropErrors = Translations['crop']['errorsText']

const UTILITY_KEY = 'crop-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MIN_CROP_SIZE = 0.04
const ALLOWED_TYPES: OutputMime[] = ['image/jpeg', 'image/png', 'image/webp']

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function normalizeRect(rect: CropRect): CropRect {
  const x = rect.width < 0 ? rect.x + rect.width : rect.x
  const y = rect.height < 0 ? rect.y + rect.height : rect.y
  return {
    x: clamp(x, 0, 1),
    y: clamp(y, 0, 1),
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  }
}

function clampRect(rect: CropRect): CropRect {
  const normalized = normalizeRect(rect)
  const width = clamp(normalized.width, MIN_CROP_SIZE, 1)
  const height = clamp(normalized.height, MIN_CROP_SIZE, 1)
  return {
    x: clamp(normalized.x, 0, 1 - width),
    y: clamp(normalized.y, 0, 1 - height),
    width,
    height,
  }
}

function presetRatio(preset: CropPresetKey): number | null {
  switch (preset) {
    case 'square':
      return 1
    case 'landscape':
      return 4 / 3
    case 'wide':
      return 16 / 9
    case 'portrait':
      return 3 / 4
    case 'free':
    default:
      return null
  }
}

function createPresetCrop(image: LoadedImage, preset: CropPresetKey): CropRect {
  const targetRatio = presetRatio(preset)
  if (!targetRatio) {
    return { x: 0.12, y: 0.12, width: 0.76, height: 0.76 }
  }

  const imageRatio = image.width / image.height
  const normalizedRatio = targetRatio / imageRatio
  let width = 0.78
  let height = width / normalizedRatio

  if (height > 0.78) {
    height = 0.78
    width = height * normalizedRatio
  }

  return {
    x: (1 - width) / 2,
    y: (1 - height) / 2,
    width,
    height,
  }
}

function pointerToUnit(event: React.PointerEvent<HTMLElement>, element: HTMLElement): Point {
  const bounds = element.getBoundingClientRect()
  return {
    x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
    y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
  }
}

function rectFromPoints(
  start: Point,
  current: Point,
  ratio: number | null,
  imageRatio: number,
): CropRect {
  if (!ratio) {
    return clampRect({
      x: Math.min(start.x, current.x),
      y: Math.min(start.y, current.y),
      width: Math.abs(current.x - start.x),
      height: Math.abs(current.y - start.y),
    })
  }

  const normalizedRatio = ratio / imageRatio
  const dx = current.x - start.x
  const dy = current.y - start.y
  const signX = dx < 0 ? -1 : 1
  const signY = dy < 0 ? -1 : 1
  let width = Math.max(Math.abs(dx), MIN_CROP_SIZE)
  let height = width / normalizedRatio

  if (height > Math.abs(dy) && Math.abs(dy) >= MIN_CROP_SIZE) {
    height = Math.abs(dy)
    width = height * normalizedRatio
  }

  return clampRect({
    x: start.x,
    y: start.y,
    width: width * signX,
    height: height * signY,
  })
}

function resizeCrop(
  crop: CropRect,
  handle: ResizeHandle,
  pointer: Point,
  ratio: number | null,
  imageRatio: number,
): CropRect {
  const anchor: Point = {
    x: handle.includes('w') ? crop.x + crop.width : crop.x,
    y: handle.includes('n') ? crop.y + crop.height : crop.y,
  }

  if (ratio) {
    return rectFromPoints(anchor, pointer, ratio, imageRatio)
  }

  const left = handle.includes('w') ? pointer.x : crop.x
  const right = handle.includes('e') ? pointer.x : crop.x + crop.width
  const top = handle.includes('n') ? pointer.y : crop.y
  const bottom = handle.includes('s') ? pointer.y : crop.y + crop.height

  return clampRect({
    x: Math.min(left, right),
    y: Math.min(top, bottom),
    width: Math.abs(right - left),
    height: Math.abs(bottom - top),
  })
}

function loadImage(src: string, errors: CropErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(errors.corrupt))
    img.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: OutputMime,
  errors: CropErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error(errors.cropFailed))
        }
      },
      mime,
      mime === 'image/png' ? undefined : 0.95,
    )
  })
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function outputFilename(file: File, mime: OutputMime): string {
  const base = file.name.replace(/\.[^.]+$/, '') || 'image'
  const ext = mime === 'image/jpeg' ? 'jpg' : mime.replace('image/', '')
  return `${base}-cropped.${ext}`
}

export function CropImagePage() {
  const t = useT()
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [crop, setCrop] = useState<CropRect>({ x: 0.12, y: 0.12, width: 0.76, height: 0.76 })
  const [preset, setPreset] = useState<CropPresetKey>('free')
  const [preview, setPreview] = useState<CropPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragRef = useRef<DragState | null>(null)

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview.url)
      }
    }
  }, [preview])

  const imageRatio = image ? image.width / image.height : 1
  const activeRatio = presetRatio(preset)
  const selectedPreset = t.crop.presets.find((item) => item.key === preset)
  const cropPixels = useMemo(() => {
    if (!image) return null
    return {
      width: Math.round(crop.width * image.width),
      height: Math.round(crop.height * image.height),
    }
  }, [crop.height, crop.width, image])

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      const mime = file.type.toLowerCase() as OutputMime

      if (!ALLOWED_TYPES.includes(mime)) {
        setError(t.crop.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t.crop.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, t.crop.errorsText)
        const nextImage: LoadedImage = {
          file,
          url,
          width: decoded.naturalWidth,
          height: decoded.naturalHeight,
          mime,
        }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
        setPreset('free')
        setCrop(createPresetCrop(nextImage, 'free'))
      } catch (err) {
        URL.revokeObjectURL(url)
        setError(err instanceof Error ? err.message : t.crop.errorsText.corrupt)
        toolError(UTILITY_KEY, 'corrupt_image')
      }
    },
    [t.crop.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handlePresetChange = useCallback(
    (nextPreset: CropPresetKey) => {
      setPreset(nextPreset)
      if (image) {
        setCrop(createPresetCrop(image, nextPreset))
      }
    },
    [image],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const handleStagePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!image) return
      const pointer = pointerToUnit(event, event.currentTarget)
      const nextCrop = clampRect({ x: pointer.x, y: pointer.y, width: MIN_CROP_SIZE, height: MIN_CROP_SIZE })
      setCrop(nextCrop)
      dragRef.current = {
        mode: 'draw',
        startPointer: pointer,
        startCrop: nextCrop,
        pointerId: event.pointerId,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [image],
  )

  const handleCropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.stopPropagation()
      dragRef.current = {
        mode: 'move',
        startPointer: pointerToUnit(event, event.currentTarget.parentElement!),
        startCrop: crop,
        pointerId: event.pointerId,
      }
      event.currentTarget.parentElement?.setPointerCapture(event.pointerId)
    },
    [crop],
  )

  const handleHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, handle: ResizeHandle) => {
      event.stopPropagation()
      const stage = event.currentTarget.closest<HTMLElement>('[data-crop-stage]')
      if (!stage) return
      dragRef.current = {
        mode: `resize-${handle}`,
        startPointer: pointerToUnit(event, stage),
        startCrop: crop,
        pointerId: event.pointerId,
      }
      stage.setPointerCapture(event.pointerId)
    },
    [crop],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag || !image || drag.pointerId !== event.pointerId) return

      const pointer = pointerToUnit(event, event.currentTarget)
      if (drag.mode === 'draw') {
        setCrop(rectFromPoints(drag.startPointer, pointer, activeRatio, imageRatio))
        return
      }

      if (drag.mode === 'move') {
        setCrop({
          ...drag.startCrop,
          x: clamp(drag.startCrop.x + pointer.x - drag.startPointer.x, 0, 1 - drag.startCrop.width),
          y: clamp(drag.startCrop.y + pointer.y - drag.startPointer.y, 0, 1 - drag.startCrop.height),
        })
        return
      }

      const handle = drag.mode.replace('resize-', '') as ResizeHandle
      setCrop(resizeCrop(drag.startCrop, handle, pointer, activeRatio, imageRatio))
    },
    [activeRatio, image, imageRatio],
  )

  const stopPointerDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  const cropToBlob = useCallback(async (): Promise<Blob> => {
    if (!image) {
      throw new Error(t.crop.errorsText.noImage)
    }

    const decoded = await loadImage(image.url, t.crop.errorsText)
    const sourceX = Math.round(crop.x * image.width)
    const sourceY = Math.round(crop.y * image.height)
    const sourceWidth = Math.max(1, Math.round(crop.width * image.width))
    const sourceHeight = Math.max(1, Math.round(crop.height * image.height))
    const canvas = document.createElement('canvas')
    canvas.width = sourceWidth
    canvas.height = sourceHeight

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error(t.crop.errorsText.canvas)
    }

    if (image.mime === 'image/jpeg') {
      context.fillStyle = '#FFFFFF'
      context.fillRect(0, 0, canvas.width, canvas.height)
    }

    context.drawImage(
      decoded,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight,
    )

    return blobFromCanvas(canvas, image.mime, t.crop.errorsText)
  }, [crop.height, crop.width, crop.x, crop.y, image, t.crop.errorsText])

  useEffect(() => {
    if (!image) {
      setPreview((current) => {
        if (current) {
          URL.revokeObjectURL(current.url)
        }
        return null
      })
      setIsPreviewing(false)
      return
    }

    let cancelled = false
    setIsPreviewing(true)
    const timer = window.setTimeout(() => {
      void cropToBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          setPreview((current) => {
            if (current) {
              URL.revokeObjectURL(current.url)
            }
            return { blob, url }
          })
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : t.crop.errorsText.cropFailed)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsPreviewing(false)
          }
        })
    }, 220)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [cropToBlob, image, t.crop.errorsText.cropFailed])

  const handleDownload = useCallback(async () => {
    if (!image) return

    const startTime = performance.now()
    const presetKey = selectedPreset?.analyticsKey ?? preset
    toolInvoke(UTILITY_KEY, { preset: presetKey })
    setIsExporting(true)
    setError(null)

    try {
      const blob = await cropToBlob()
      downloadBlob(blob, outputFilename(image.file, image.mime))
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        preset: presetKey,
        width: Math.round(crop.width * image.width),
        height: Math.round(crop.height * image.height),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.crop.errorsText.cropFailed)
      toolError(UTILITY_KEY, 'crop_failed', { preset: presetKey })
    } finally {
      setIsExporting(false)
    }
  }, [crop.height, crop.width, cropToBlob, image, preset, selectedPreset?.analyticsKey, t.crop.errorsText.cropFailed])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={t.crop.page.eyebrow}
        title={t.crop.page.title}
        description={t.crop.page.description}
        chips={[t.crop.formats, t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          {!image && (
            <Card
              className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-4 text-center sm:p-8">
                <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                  IMG
                </div>
                <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.crop.dropTitle}</p>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                  {t.crop.dropDescription}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />
                <Button onClick={() => inputRef.current?.click()}>
                  {t.crop.chooseFile}
                </Button>
              </CardContent>
            </Card>
          )}

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                      {image.file.name}
                    </h2>
                    <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {image.mime.replace('image/', '').toUpperCase()}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(image.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {image.width} x {image.height}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()}>
                    {t.crop.replaceImage}
                  </Button>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />

                <div
                  data-crop-stage
                  className="relative mx-auto w-full max-w-4xl touch-none select-none overflow-hidden rounded-md border bg-muted"
                  style={{ aspectRatio: `${image.width} / ${image.height}` }}
                  onPointerDown={handleStagePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={stopPointerDrag}
                  onPointerCancel={stopPointerDrag}
                >
                  <img
                    src={image.url}
                    alt={image.file.name}
                    className="h-full w-full object-fill"
                    draggable={false}
                  />
                  <div
                    className="absolute cursor-move border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                    style={{
                      left: `${crop.x * 100}%`,
                      top: `${crop.y * 100}%`,
                      width: `${crop.width * 100}%`,
                      height: `${crop.height * 100}%`,
                    }}
                    onPointerDown={handleCropPointerDown}
                  >
                    <div className="grid h-full w-full grid-cols-3 grid-rows-3">
                      {Array.from({ length: 9 }).map((_, index) => (
                        <span key={index} className="border border-white/35" aria-hidden />
                      ))}
                    </div>
                    {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((handle) => (
                      <button
                        key={handle}
                        type="button"
                        aria-label={t.crop.resizeHandle}
                        className={cn(
                          'absolute h-5 w-5 rounded-full border-2 border-white bg-primary shadow-sm',
                          handle === 'nw' && '-left-2.5 -top-2.5 cursor-nwse-resize',
                          handle === 'ne' && '-right-2.5 -top-2.5 cursor-nesw-resize',
                          handle === 'sw' && '-bottom-2.5 -left-2.5 cursor-nesw-resize',
                          handle === 'se' && '-bottom-2.5 -right-2.5 cursor-nwse-resize',
                        )}
                        onPointerDown={(event) => handleHandlePointerDown(event, handle)}
                      />
                    ))}
                  </div>
                </div>

                <div className="hidden gap-4 md:grid md:grid-cols-2">
                  <PreviewPanel
                    label={t.crop.sourceLabel}
                    src={image.url}
                    name={image.file.name}
                    size={formatSize(image.file.size)}
                    dimensions={`${image.width} x ${image.height}`}
                  />
                  <PreviewPanel
                    label={t.crop.resultLabel}
                    src={preview?.url ?? image.url}
                    name={preview ? outputFilename(image.file, image.mime) : t.crop.waitingPreview}
                    size={
                      preview
                        ? formatSize(preview.blob.size)
                        : isPreviewing
                          ? t.crop.previewUpdating
                          : t.crop.notReady
                    }
                    dimensions={`${cropPixels?.width ?? 0} x ${cropPixels?.height ?? 0}`}
                    muted={!preview}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{t.crop.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{t.crop.aspectRatio}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {t.crop.presets.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        className={cn(
                          'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                          preset === item.key
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'bg-background text-foreground hover:bg-muted',
                        )}
                        onClick={() => handlePresetChange(item.key as CropPresetKey)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">
                      {cropPixels?.width ?? 0}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.crop.width}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">
                      {cropPixels?.height ?? 0}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.crop.height}</div>
                  </div>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleDownload}
                  disabled={isExporting}
                >
                  {isExporting ? t.crop.exporting : t.crop.download}
                </Button>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.crop.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {t.crop.page.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  muted = false,
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  muted?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className={cn('aspect-video bg-muted', muted && 'opacity-55')}>
        <img src={src} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
          </span>
        </div>
      </div>
    </div>
  )
}
